import React, { Dispatch } from 'react';
import { connect } from 'react-redux';
import __ from '@vitafy/common-libraries/frontend/Translate';
import { ReviewAppState } from '../redux/reducers';
import { FetchReviewsActionPayload, switchPage } from '../redux/actions';
import { ReviewsThunk } from '../redux/thunks';
import Review from '../../server/domain/Review';

type PaginationProps = {
  currentPage: number;
  fetchReviews?: Function;
  locale?: string;
  reviewsMap?: Map<number, Review[]>;
  totalPages: number;
};

const config = {
  frameLength: 5,
  frameLengthHalf: Math.ceil(5 / 2)
};

/**
 * get the page numbers currently to display in the main-part of pagination
 *
 * e.g. the resulting appearance should be as follows:
 * - on p.1: 1 | 2 | 3 | 4 | 5 | … | 7 | >>
 * - on p.2: << | 1 | 2 | 3 | 4 | 5 | … | 7 | >>
 * - on p.6: << | 1 | … | 3 | 4 | 5 | 6 | 7 | >>
 * - on p.7: << | 1 | … | 3 | 4 | 5 | 6 | 7
 *
 * @param {number} page
 * @param {number} totalPages
 * @return {Array}
 *
 */

const currentFrame = function (page: number, totalPages: number): number[] {
  let start = 1;
  let end: number = totalPages;

  if (totalPages > config.frameLength) {
    const half: number = config.frameLengthHalf;

    if (page >= half && page <= totalPages - half) {
      // if halfed-frameLength neither reaches 1 or totalPages
      start = page - half + 1;
      end = start + config.frameLength - 1;
    } else if (page < half) {
      // if halfed-frameLength reaches 1
      start = 1;
      end = config.frameLength;
    } else if (page > totalPages - half) {
      // if halfed-frameLength reaches totalPages
      end = totalPages;
      start = end - config.frameLength + 1;
    }
  }

  const frame = [];
  for (let i = start; i <= end; i += 1) {
    frame.push(i);
  }
  return frame;
};

const isFirstPage = function (page: number): boolean {
  return page === 1;
};
const canShowFirst = function (page: number, totalPages: number): boolean {
  return currentFrame(page, totalPages)[0] >= config.frameLengthHalf - 1;
};

const canShowPreviousJump = function (page: number, totalPages: number): boolean {
  return currentFrame(page, totalPages)[0] >= config.frameLengthHalf;
};

const getPreviousJump = function (page: number, totalPages: number): number {
  const currFrame = currentFrame(page, totalPages);
  return 1 + Math.floor(currFrame[0] / 2);
};

const isLastPage = function (page: number, totalPages: number): boolean {
  return page == totalPages;
};

const canShowLast = function (page: number, totalPages: number): boolean {
  return currentFrame(page, totalPages)[1] < totalPages - config.frameLengthHalf;
};

const canShowNextJump = function (page: number, totalPages: number): boolean {
  const halfFloored: number = Math.floor(config.frameLength / 2);
  const currFrame = currentFrame(page, totalPages);
  return currFrame[currFrame.length - 1] <= totalPages - halfFloored;
};

const getNextJump = function (page: number, totalPages: number): number {
  const currFrame = currentFrame(page, totalPages);
  return totalPages - Math.floor((totalPages - currFrame[currFrame.length - 1]) / 2);
};

const framePages = (
  page: number,
  totalPages: number,
  fetchReviews: Function,
  reviewsMap: Map<number, Review[]>
) =>
  currentFrame(page, totalPages).map((n: number, index: number) => (
    <li key={index} className={`pagination__item ${n == page ? 'pagination__item--active' : ''}`}>
      <a
        href=""
        onClick={(e) => {
          e.preventDefault();
          fetchReviews({ page: n, reviewsMap });
          // manual focus removal needed, since otherwise item would have focus
          // after LOAD_REVIEWS_SUCCESS although item's number changed
          e.currentTarget.blur();
        }}
      >
        {n}
      </a>
    </li>
  ));

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  fetchReviews,
  locale,
  totalPages,
  reviewsMap
}) => (
  <nav className="pagination">
    <ul>
      {!isFirstPage(currentPage) && (
        <li className="pagination__item">
          <a
            href=""
            title={__({ val: 'Previous step', locale })}
            onClick={(e) => {
              e.preventDefault();
              fetchReviews({ page: currentPage - 1, reviewsMap });
            }}
          >
            &laquo;
          </a>
        </li>
      )}
      {canShowFirst(currentPage, totalPages) && (
        <li className="pagination__item">
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              fetchReviews({ page: 1, reviewsMap });
            }}
          >
            1
          </a>
        </li>
      )}
      {canShowPreviousJump(currentPage, totalPages) && (
        <li className="pagination__item">
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              fetchReviews({
                page: getPreviousJump(currentPage, totalPages),
                reviewsMap
              });
            }}
          >
            …
          </a>
        </li>
      )}

      {framePages(currentPage, totalPages, fetchReviews, reviewsMap)}

      {canShowNextJump(currentPage, totalPages) && (
        <li className="pagination__item">
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              fetchReviews({
                page: getNextJump(currentPage, totalPages),
                reviewsMap
              });
            }}
          >
            …
          </a>
        </li>
      )}
      {canShowLast(currentPage, totalPages) && (
        <li className="pagination__item">
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              fetchReviews({ page: totalPages, reviewsMap });
            }}
          >
            {totalPages}
          </a>
        </li>
      )}
      {!isLastPage(currentPage, totalPages) && (
        <li className="pagination__item">
          <a
            href=""
            title={__({ val: 'Previous step', locale })}
            onClick={(e) => {
              e.preventDefault();
              fetchReviews({ page: currentPage + 1, reviewsMap });
            }}
          >
            &raquo;
          </a>
        </li>
      )}
    </ul>
  </nav>
);

interface PaginationStateFromProps {
  fetchReviews?: any;
  locale: string;
  currentPage: number;
  totalPages: number;
  reviewsMap: Map<number, Review[]>;
}

const mapStateToProps = (state: ReviewAppState, ownProps: any) => ({
  fetchReviews: ownProps.fetchReviews,
  locale: state.locale,
  currentPage: state.page,
  totalPages: state.totalPages,
  reviewsMap: state.reviewsMap
});

interface DispatchFromProps {
  fetchReviews: (payload: FetchReviewsActionPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchFromProps => {
  return {
    fetchReviews: (payload: FetchReviewsActionPayload) => {
      if (payload.reviewsMap && payload.reviewsMap.get(payload.page)) {
        dispatch(switchPage(payload));
      } else {
        dispatch(ReviewsThunk(payload));
      }
    }
  };
};

export default connect<PaginationStateFromProps, DispatchFromProps>(
  mapStateToProps,
  mapDispatchToProps
)(Pagination);
